/**
 * Per-section config + content editor. Switches on the section's content type:
 * a read-only config summary (+ compact preview) in view mode, and type-specific
 * controls in edit mode (rich text for static / AI-sample prose, column labels for
 * data tables, parties for signatures, level for a TOC). Section/sample mutations
 * are handed up to the workbench.
 */

import type { SignatureFieldKey } from '@protoss/doc';
import { tiptapToDocumentChildren } from '@protoss/doc';
import { Button, NumberInput, PillSelect, RichTextEditor, TextField } from '@protoss/ui';
import * as React from 'react';
import { DocContractView, type DocHeading, type PreviewSample } from '../../../../components/doc';
import { parseContentToTiptap, tiptapToMarkdown } from '../../../../lib/tiptap';
import {
  aiDataKey,
  sectionBodyToMarkdown,
  signatureBlock,
  tableBlock,
  tableColumnKeys,
  tocBlock,
  updateSectionBlock,
  withBody,
  type LogicalSection,
} from '../../lib/sowContract';

const SIGNATURE_FIELDS: SignatureFieldKey[] = ['company', 'name', 'title', 'signature', 'date'];

export interface SowSectionConfigEditorProps {
  section: LogicalSection;
  isEditing: boolean;
  sample: PreviewSample;
  headings: DocHeading[];
  onChange: (next: LogicalSection) => void;
  onSampleChange: (next: PreviewSample) => void;
}

export function SowSectionConfigEditor(props: SowSectionConfigEditorProps) {
  const { section, isEditing } = props;
  if (!isEditing) return <SectionConfigView {...props} />;
  switch (section.contentType) {
    case 'static':
      return <StaticEditor {...props} />;
    case 'ai':
      return <AiEditor {...props} />;
    case 'table':
      return <TableEditor {...props} />;
    case 'signature':
      return <SignatureEditor {...props} />;
    case 'toc':
      return <TocEditor {...props} />;
    default:
      return <SectionConfigView {...props} />;
  }
}

// ── View ──────────────────────────────────────────────────────────────────────

function SectionConfigView({ section, sample, headings }: SowSectionConfigEditorProps) {
  return (
    <div className="ptp-sow-section__view">
      <ConfigFacts section={section} />
      {section.contentType !== 'break' && section.body.length > 0 ? (
        <div className="ptp-sow-section__inline-preview">
          <DocContractView content={section.body} sample={sample} mode="sample" headings={headings} />
        </div>
      ) : null}
    </div>
  );
}

function ConfigFacts({ section }: { section: LogicalSection }) {
  const facts: Array<[string, string]> = [];
  switch (section.contentType) {
    case 'ai':
      facts.push(['Narrative source', aiDataKey(section) ?? '-']);
      break;
    case 'table': {
      const block = tableBlock(section);
      if (block) {
        facts.push(['Data binding', block.dataKey]);
        facts.push(['Columns', String(tableColumnKeys(block).length)]);
      }
      break;
    }
    case 'signature':
      facts.push(['Parties', String(signatureBlock(section)?.parties.length ?? 0)]);
      facts.push(['Mode', signatureBlock(section)?.mode ?? 'visual']);
      break;
    case 'toc':
      facts.push(['Depth', `Levels 1-${tocBlock(section)?.maxLevel ?? 3}`]);
      break;
    case 'mixed':
      facts.push(['Note', 'Literal content alongside a generated block']);
      break;
    default:
      break;
  }
  if (facts.length === 0) return null;
  return (
    <dl className="ptp-sow-section__facts">
      {facts.map(([k, v]) => (
        <div key={k} className="ptp-sow-section__fact">
          <dt>{k}</dt>
          <dd>{v}</dd>
        </div>
      ))}
    </dl>
  );
}

// ── Static prose ───────────────────────────────────────────────────────────────

function StaticEditor({ section, onChange }: SowSectionConfigEditorProps) {
  // Seed the editor once per section so typing never resets the cursor.
  const seed = React.useMemo(
    () => parseContentToTiptap(sectionBodyToMarkdown(section)),
    [section.id] // eslint-disable-line react-hooks/exhaustive-deps
  );
  return (
    <div className="ptp-sow-section__editor">
      <RichTextEditor
        content={seed}
        toolbar
        minHeight="180px"
        onUpdate={(json) => onChange(withBody(section, tiptapToDocumentChildren(json)))}
      />
    </div>
  );
}

// ── AI-generated (edit the sample/default narrative used by the preview) ─────────

function AiEditor({ section, sample, onSampleChange }: SowSectionConfigEditorProps) {
  const dataKey = aiDataKey(section) ?? '';
  const seed = React.useMemo(
    () => parseContentToTiptap(sample.narrative[dataKey] ?? ''),
    [section.id] // eslint-disable-line react-hooks/exhaustive-deps
  );
  return (
    <div className="ptp-sow-section__editor">
      <p className="ptp-sow-section__hint">
        Written per deal by the SOW generator. Edit the sample text shown in the preview below.
      </p>
      <RichTextEditor
        content={seed}
        toolbar
        minHeight="160px"
        onUpdate={(json) =>
          onSampleChange({
            ...sample,
            narrative: { ...sample.narrative, [dataKey]: tiptapToMarkdown(json) },
          })
        }
      />
    </div>
  );
}

// ── Data table (edit column labels; rows live in the mock-data drawer) ───────────

function TableEditor({ section, onChange }: SowSectionConfigEditorProps) {
  const block = tableBlock(section);
  const headerRow = block?.headerRows?.[0];
  if (!block || !headerRow) {
    return <p className="ptp-sow-section__hint">This table has no editable header row.</p>;
  }
  const setHeaderLabel = (cellIndex: number, label: string) => {
    onChange(
      updateSectionBlock(section, 'dynamicBlock', (b) => {
        const cell = b.headerRows?.[0]?.children[cellIndex];
        const para = cell?.children.find((c) => c.type === 'paragraph');
        if (para && para.type === 'paragraph') para.children = [{ text: label }];
      })
    );
  };
  const cellLabel = (cellIndex: number): string => {
    const para = headerRow.children[cellIndex]?.children.find((c) => c.type === 'paragraph');
    if (para && para.type === 'paragraph') {
      const run = para.children.find((r) => !('type' in r));
      return run && !('type' in run) ? (run.text ?? '') : '';
    }
    return '';
  };
  return (
    <div className="ptp-sow-section__editor">
      <p className="ptp-sow-section__hint">
        Column headings for <code>{block.dataKey}</code>. Edit the sample rows from the preview&apos;s
        &ldquo;Edit mock data&rdquo; menu.
      </p>
      <div className="ptp-sow-section__columns">
        {headerRow.children.map((_, i) => (
          <label key={i} className="ptp-sow-section__column">
            <span className="ptp-sow-section__column-label">Column {i + 1}</span>
            <TextField value={cellLabel(i)} onChange={(e) => setHeaderLabel(i, e.target.value)} size="compact" />
          </label>
        ))}
      </div>
    </div>
  );
}

// ── Signature ────────────────────────────────────────────────────────────────

function SignatureEditor({ section, onChange }: SowSectionConfigEditorProps) {
  const block = signatureBlock(section);
  if (!block) return null;

  const updateParty = (index: number, mutate: (p: NonNullable<typeof block>['parties'][number]) => void) =>
    onChange(
      updateSectionBlock(section, 'signatureBlock', (b) => {
        const party = b.parties[index];
        if (party) mutate(party);
      })
    );

  const addParty = () =>
    onChange(
      updateSectionBlock(section, 'signatureBlock', (b) => {
        b.parties.push({ label: 'Party', fields: ['name', 'title', 'signature', 'date'] });
      })
    );

  const removeParty = (index: number) =>
    onChange(
      updateSectionBlock(section, 'signatureBlock', (b) => {
        b.parties.splice(index, 1);
      })
    );

  return (
    <div className="ptp-sow-section__editor ptp-sow-section__parties">
      {block.parties.map((party, i) => (
        <div key={i} className="ptp-sow-section__party">
          <div className="ptp-sow-section__party-head">
            <TextField
              value={party.label ?? ''}
              onChange={(e) => updateParty(i, (p) => (p.label = e.target.value))}
              placeholder="Party label"
              size="compact"
            />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => removeParty(i)}
              disabled={block.parties.length <= 1}
            >
              Remove
            </Button>
          </div>
          <PillSelect
            options={SIGNATURE_FIELDS.map((f) => ({ value: f, label: f }))}
            selectedValues={party.fields}
            onSelectionChange={(values) =>
              updateParty(i, (p) => (p.fields = values as SignatureFieldKey[]))
            }
            label="Fields"
          />
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" onClick={addParty}>
        + Add party
      </Button>
    </div>
  );
}

// ── Table of contents ──────────────────────────────────────────────────────────

function TocEditor({ section, onChange }: SowSectionConfigEditorProps) {
  const block = tocBlock(section);
  if (!block) return null;
  return (
    <div className="ptp-sow-section__editor ptp-sow-section__toc">
      <label className="ptp-sow-section__column">
        <span className="ptp-sow-section__column-label">Title</span>
        <TextField
          value={typeof block.title === 'string' ? block.title : ''}
          onChange={(e) =>
            onChange(updateSectionBlock(section, 'toc', (b) => (b.title = e.target.value)))
          }
          placeholder="Table of Contents"
          size="compact"
        />
      </label>
      <label className="ptp-sow-section__column">
        <span className="ptp-sow-section__column-label">Deepest level</span>
        <NumberInput
          value={block.maxLevel ?? 3}
          min={1}
          max={6}
          step={1}
          size="compact"
          onChange={(e) =>
            onChange(
              updateSectionBlock(section, 'toc', (b) => {
                b.maxLevel = Math.min(6, Math.max(1, parseInt(e.target.value, 10) || 3));
              })
            )
          }
        />
      </label>
    </div>
  );
}
